/**
 * Perception: a detection meter per soldier (02 §6.2). Individuals see; the garrison as a
 * whole only reacts to noise (alert.ts). Also where a soldier finds a body.
 */

import { bark, raiseAlert, sendToInvestigate } from '../alert';
import type { Ctx } from '../ctx';
import { rangerLit, rangerSubmerged } from '../hazards/terrain';
import { angleDiff, atan2, PI } from '../math';
import { disguiseHides } from '../objectives/disguise';
import { clearSight } from '../smoke';
import { inTrench } from '../statics';
import { ENEMY } from '../tuning';
import { DT, type Enemy } from '../types';
import { stormVision } from '../weather';
import { concealment, hearRange, sightHeight } from './stealth';

export const DEG = PI / 180;

export const EYE = 1.6;

/** Where the detection falloff starts, metres: nearer than this a man is seen at full speed. */
const FALLOFF_FROM = 6;

export function perceive(ctx: Ctx, e: Enemy): void {
  const { world: w, statics } = ctx;
  const r = w.ranger;
  const dtp = ENEMY.perceptionEveryTicks * DT;
  // Aboard the Osprey or under canopy he is nowhere an infantryman is looking.
  const onGround = w.insertion.phase === 'ground';
  // HAZ: crawling under water he is hidden completely (02 §6.1), for as long as his breath lasts.
  const visibleStatus = onGround && !rangerSubmerged(w)
    && (r.status === 'active' || r.status === 'downed' || r.status === 'boarding');
  const dx = r.x - e.x;
  const dz = r.z - e.z;
  const dist = Math.sqrt(dx * dx + dz * dz);
  // HAZ: held in a searchlight beam he is seen as if by day, whatever the hour (02 §9.2).
  const light = rangerLit(w) ? 'day' : statics.def.timeOfDay;
  // R5: a whiteout, a sandstorm or a thunderstorm halves what anyone can see - the Storm modifier's
  // own promise, which the sim never kept (ROUND5 §3.2). The ranger's half of it is the weather he
  // is looking through. A thunderstorm throws lightning, and for the instant of a stroke the cover
  // the weather was lending him is gone: the map is lit and everyone sees the length of it.
  const def = statics.def;
  const range = ENEMY.range[light] * (e.mans !== null ? 1.1 : 1) * stormVision(def, def.seed, w.tick);
  const eInTrench = inTrench(statics.def, e.x, e.z);
  // ROUND7: behind him, a man notices the ranger only by what he hears, and how close that is depends on
  // how the ranger moves - a man standing still makes no sound at all (STEALTH.hear). This was an all-round
  // 2.2 m bubble (`ENEMY.senseRange`) inside which nobody could ever be knifed unaware. A gunner in his
  // nest watches his arc and nothing else, as before.
  const hear = hearRange(w, def);
  const heard = e.mans === null && hear > 0 && dist <= hear;

  let sees = false;
  if (visibleStatus && (dist <= range || heard)) {
    const off = Math.abs(angleDiff(e.facing, atan2(dx, dz)));
    const arc = e.mans !== null ? ENEMY.emplacementArcDeg : ENEMY.fovDeg / 2;
    const inCone = (off <= arc * DEG && dist <= range) || heard;
    // The sacred rule (01 §11): crawling in a trench hides you from everyone outside it.
    const hiddenInTrench = r.inTrench && r.stance === 'crawl' && !eInTrench;
    if (inCone && !hiddenInTrench) {
      // ROUND7: crouched he shows less of himself: a sandbag run or a hedge hides a sneaker (stealth.ts).
      sees = clearSight(ctx, e.x, e.z, EYE, r.x, r.z, sightHeight(r), e.mans ?? -1);
    }
  }
  // OBJ: a ranger in a stolen uniform is just another soldier to infantry, until scrutiny
  // or his own gunfire ends it (02 §6.4). Automated kinds and men already fighting him are not fooled.
  if (sees && disguiseHides(w, e)) sees = false;
  e.seesRanger = sees;

  if (sees) {
    e.lostTicks = 0;
    if (dist <= ENEMY.instantRange) e.detection = 1;
    else {
      // ROUND7: crouched or flat, in a ghillie wrap, in the dark - how hard he is to make out (stealth.ts).
      const concealed = concealment(w, def);
      // A man hauling a body is slow, upright and obvious.
      const motion = r.dragging >= 0 ? 1.6 : !r.moving ? 0.5 : r.running ? 1.8 : 1;
      // A night storm pulls the range under `FALLOFF_FROM`; without the floor the divisor turns
      // negative and the man two metres away becomes the hardest of all to see.
      const span = Math.max(1, range - FALLOFF_FROM);
      const falloff = 1 - 0.7 * Math.min(1, Math.max(0, (dist - FALLOFF_FROM) / span));
      const mood = (e.wary ? 1.5 : 1) * (1 + 0.25 * w.alert);
      e.detection = Math.min(1, e.detection + ENEMY.detectPerSecond * concealed * motion * falloff * mood * dtp);
    }
    e.targetX = r.x;
    e.targetZ = r.z;
  } else {
    e.lostTicks += ENEMY.perceptionEveryTicks;
    if (e.state !== 'combat') e.detection = Math.max(0, e.detection - ENEMY.forgetPerSecond * dtp);
  }

  // Finding a body puts the garrison on edge - unless it was dragged out of sight.
  if (e.state === 'guard' || e.state === 'patrol' || e.state === 'return') {
    for (const body of w.enemies) {
      if (body.state !== 'dead' || body.found) continue;
      const bx = body.x - e.x;
      const bz = body.z - e.z;
      if (bx * bx + bz * bz > ENEMY.bodySightRange * ENEMY.bodySightRange) continue;
      if (Math.abs(angleDiff(e.facing, atan2(bx, bz))) > (ENEMY.fovDeg / 2) * DEG) continue;
      if (inTrench(statics.def, body.x, body.z) && !eInTrench) continue;
      if (!clearSight(ctx, e.x, e.z, EYE, body.x, body.z, 0.3)) continue;
      body.found = true;
      raiseAlert(ctx, 1, body.x, body.z);
      bark(ctx, e, 'manDown');
      sendToInvestigate(e, body.x, body.z, true);
      break;
    }
  }
}
